import { IChannel, IPoll } from "@Interfaces";

/**
 * Namespaced key prefix for the author-scoped list of completed channel ids.
 */
export const COMPLETED_CHANNELS_STORAGE_PREFIX = "tb.completedChannels";
export const CHANNEL_MILESTONE_TARGET = 5;

/**
 * True when the participant voted on the poll in this session: a selected choice or a
 * confirmed vote. First-round votes count, so double-vote polls count after the first pass.
 * Shared by the all-polls-completed toast and the completion redirect.
 */
export const hasVotedOnPoll = (poll: IPoll): boolean => {
    const hasChoice = Array.isArray(poll.user_choice)
        ? poll.user_choice.length > 0
        : Boolean(poll.user_choice);
    return hasChoice || Boolean(poll.vote_id);
};

type ChannelAdminShape =
    | string
    | {
          _id?: string;
          id?: string;
          username?: string;
          name?: string;
          full_name?: string;
      };

/**
 * Returns the admin we consider "primary" for completion tracking.
 * Currently channels expose a single admin, so we always use index 0.
 */
const getPrimaryAdmin = (
    channel: IChannel | null | undefined
): ChannelAdminShape | null => {
    if (
        !channel ||
        !Array.isArray(channel.admins) ||
        channel.admins.length === 0
    ) {
        return null;
    }

    return channel.admins[0] as ChannelAdminShape;
};

/**
 * Builds the `localStorage` key scoped to a specific author id.
 */
const buildStorageKey = (authorId: string | null): string | null => {
    if (!authorId) {
        return null;
    }

    return `${COMPLETED_CHANNELS_STORAGE_PREFIX}.${authorId}`;
};

/**
 * Extracts the author identifier from the channel admins and returns a stable id.
 */
export const resolveAuthorId = (
    channel: IChannel | null | undefined
): string | null => {
    const primaryAdmin = getPrimaryAdmin(channel);
    if (!primaryAdmin) {
        return null;
    }

    if (typeof primaryAdmin === "string") {
        return primaryAdmin;
    }

    const { _id, id } = primaryAdmin;
    return _id || id || null;
};

/**
 * Picks the best available display name for the channel author.
 */
export const resolveAuthorName = (
    channel: IChannel | null | undefined,
    fallbackName: string | null = null
): string | null => {
    const primaryAdmin = getPrimaryAdmin(channel);
    if (!primaryAdmin || typeof primaryAdmin === "string") {
        return fallbackName;
    }

    const { username, name, full_name } = primaryAdmin;
    return username || name || full_name || fallbackName;
};

/**
 * Reads the persisted list of completed channel ids for the provided author.
 */
export const loadCompletedChannels = (authorId: string | null): string[] => {
    const storageKey = buildStorageKey(authorId);
    if (!storageKey) {
        return [];
    }

    try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) {
            return [];
        }

        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Persists the latest list of completed channel ids for the provided author.
 */
export const persistCompletedChannels = (
    authorId: string | null,
    channelIds: string[]
): void => {
    const storageKey = buildStorageKey(authorId);
    if (!storageKey) {
        return;
    }

    try {
        localStorage.setItem(storageKey, JSON.stringify(channelIds));
    } catch {
        // Persistence is best-effort; ignore failures.
    }
};
